import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import '../../styles/theme.css';
import { sendContactEmail } from '../../utils/firebase';

// Responsive styles for mobile: match ContactFormStandalone.js
const responsiveFormStyles = `
@media (max-width: 700px) {
	.contact-card {
		max-width: 83vw !important;
		width: 83vw !important;
		padding: 15px 3vw !important;
		border-radius: 10px !important;
		box-shadow: 0 3px 12px rgba(43, 66, 120, 0.07) !important;
		max-height: 90vh !important;
		overflow-y: auto !important;
		-webkit-overflow-scrolling: touch !important;
	}
	.contact-form .form-row {
		flex-direction: column !important;
		gap: 0 !important;
	}
	.contact-form .form-row > div {
		width: 100% !important;
		margin-bottom: 10px !important;
	}
	.about-btn {
		width: 100% !important;
		font-size: 13.5px !important;
		padding: 10px 0 !important;
		margin-bottom: 30px !important;
	}
}
`;

const Contact = () => {
	const [form, setForm] = useState({
		name: '',
		phone: '',
		email: '',
		date: '',
		time: '',
		challenge: '',
		otherChallenge: ''
	});
	const [showOther, setShowOther] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const handleChange = (e) => {
		const { name, value } = e.target;
		// Clear error when user starts typing
		if (error) setError('');
		
		if (name === 'challenge') {
			setShowOther(value === 'Others');
			setForm((prev) => ({ ...prev, challenge: value, otherChallenge: '' }));
		} else if (name === 'otherChallenge') {
			// Limit to 100 words
			const words = value.split(/\s+/).filter(Boolean);
			if (words.length <= 100) {
				setForm((prev) => ({ ...prev, otherChallenge: value }));
			}
		} else {
			setForm((prev) => ({ ...prev, [name]: value }));
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		
		// Validate word count for other challenge
		if (showOther && form.otherChallenge.trim().split(/\s+/).filter(Boolean).length > 100) {
			return;
		}

		setLoading(true);
		setError('');

		try {
			// Call Firebase Cloud Function
			await sendContactEmail({
				name: form.name,
				phone: form.phone,
				email: form.email,
				date: form.date,
				time: form.time,
				challenge: form.challenge,
				otherChallenge: form.otherChallenge,
			});

			// Success - reset form and show success message
			setSubmitted(true);
			setForm({ 
				name: '', 
				phone: '', 
				email: '', 
				date: '', 
				time: '', 
				challenge: '', 
				otherChallenge: '' 
			});
			setShowOther(false);

			// Reset success message after 5 seconds
			setTimeout(() => {
				setSubmitted(false);
			}, 5000);
		} catch (err) {
			console.error('Error submitting form:', err);
			setError(err.message || 'Failed to send your request. Please try again or contact us directly.');
		} finally {
			setLoading(false);
		}
	};

		return (
			<>
				<Helmet>
					<title>Contact Us | OptiBill Analytics - Request Free Consultation</title>
					<meta name="description" content="Contact OptiBill Analytics for a free consultation on medical billing and RCM services. Schedule a meeting to discuss your revenue cycle challenges and solutions." />
					<meta name="keywords" content="contact medical billing, free consultation, RCM services inquiry, revenue cycle management contact, medical billing consultation" />
					<meta property="og:title" content="Contact Us | OptiBill Analytics" />
					<meta property="og:description" content="Request a free consultation to discuss your medical billing and revenue cycle management needs." />
					<meta property="og:type" content="website" />
					<meta property="og:url" content="https://www.optibillanalytics.com/contact" />
					<link rel="canonical" href="https://www.optibillanalytics.com/contact" />
				</Helmet>
				<style>{responsiveFormStyles}</style>
				<div className="contact-section bg-light-gray" style={{ minHeight: '100vh', padding: '64px 0' }}>
					<div className="contact-card" style={{ maxWidth: 960, margin: '0 auto', padding: '40px 32px', background: '#fff', borderRadius: 18, boxShadow: '0 8px 32px rgba(43, 66, 120, 0.07)', border: '2.5px solid #2EB8A8' }}>
						<h2 className="text-navy fw-bold" style={{ textAlign: 'center', marginBottom: 8 }}>Schedule Your Free Consultation</h2>
						<div className="about-divider" style={{ margin: '0 auto 24px auto' }} />
						<form className="contact-form" onSubmit={handleSubmit}>
							{/* Row 1: Name, Phone */}
							<div className="form-row" style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
								<div style={{ flex: 1 }}>
									<label className="fw-semibold text-navy" htmlFor="name">Your Name</label>
									<input
										className="form-control"
										style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6 }}
										type="text"
										id="name"
										name="name"
										value={form.name}
										onChange={handleChange}
										required
										placeholder="Enter your name"
									/>
								</div>
								<div style={{ flex: 1 }}>
									<label className="fw-semibold text-navy" htmlFor="phone">Phone Number</label>
									<input
										className="form-control"
										style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6 }}
										type="tel"
										id="phone"
										name="phone"
										value={form.phone}
										onChange={handleChange}
										required
										placeholder="Enter your phone number"
									/>
								</div>
							</div>
							{/* Row 2: Email, Date, Time */}
							<div className="form-row" style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
								<div style={{ flex: 2 }}>
									<label className="fw-semibold text-navy" htmlFor="email">Email Address</label>
									<input
										className="form-control"
										style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6 }}
										type="email"
										id="email"
										name="email"
										value={form.email}
										onChange={handleChange}
										required
										placeholder="Enter your email address"
									/>
								</div>
								<div style={{ flex: 1 }}>
									<label className="fw-semibold text-navy" htmlFor="date">Select Date</label>
									<input
										className="form-control"
										style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6 }}
										type="date"
										id="date"
										name="date"
										value={form.date}
										onChange={handleChange}
										required
									/>
								</div>
								<div style={{ flex: 1 }}>
									<label className="fw-semibold text-navy" htmlFor="time">Select Time</label>
								<select
									className="form-control"
									style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6 }}
									id="time"
									name="time"
									value={form.time}
									onChange={handleChange}
									required
								>
									<option value="" disabled>Choose a time</option>
									<option value="9:00 AM">9:00 AM</option>
									<option value="9:30 AM">9:30 AM</option>
									<option value="10:00 AM">10:00 AM</option>
									<option value="10:30 AM">10:30 AM</option>
									<option value="11:00 AM">11:00 AM</option>
									<option value="11:30 AM">11:30 AM</option>
									<option value="12:00 PM">12:00 PM</option>
									<option value="12:30 PM">12:30 PM</option>
									<option value="1:00 PM">1:00 PM</option>
									<option value="1:30 PM">1:30 PM</option>
									<option value="2:00 PM">2:00 PM</option>
									<option value="2:30 PM">2:30 PM</option>
									<option value="3:00 PM">3:00 PM</option>
									<option value="3:30 PM">3:30 PM</option>
									<option value="4:00 PM">4:00 PM</option>
									<option value="4:30 PM">4:30 PM</option>
									<option value="5:00 PM">5:00 PM</option>
									<option value="5:30 PM">5:30 PM</option>
									<option value="6:00 PM">6:00 PM</option>
								</select>
								</div>
							</div>
							{/* Row 3: Challenge dropdown and conditional textbox */}
							<div style={{ marginBottom: 24 }}>
								<label className="fw-semibold text-navy" htmlFor="challenge">What is your biggest revenue cycle challenge?</label>
								<select
									className="form-control"
									style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6 }}
									id="challenge"
									name="challenge"
									value={form.challenge}
									onChange={handleChange}
									required
								>
									<option value="" disabled>Select a challenge</option>
									<option value="High Denial Rate">High Denial Rate</option>
									<option value="Slow Reimbursement">Slow Reimbursement</option>
									<option value="Aging Accounts Receivable">Aging Accounts Receivable</option>
									<option value="Inefficient Coding Processes">Inefficient Coding Processes</option>
									<option value="Prior Authorization Delays">Prior Authorization Delays</option>
									<option value="Patient Payment Collection">Patient Payment Collection</option>
									<option value="Insurance Verification Issues">Insurance Verification Issues</option>
									<option value="Understaffed Billing Team">Understaffed Billing Team</option>
									<option value="Lack of Reporting & Analytics">Lack of Reporting & Analytics</option>
									<option value="Revenue Leakage">Revenue Leakage</option>
									<option value="Credentialing Issues">Credentialing Issues</option>
									<option value="Others">Others</option>
								</select>
								{showOther && (
									<div style={{ marginTop: 16 }}>
										<label className="fw-semibold text-navy" htmlFor="otherChallenge">Describe Your Challenge (max 100 words)</label>
										<textarea
											className="form-control"
											style={{ width: '100%', padding: '12px', borderRadius: 12, border: '1.5px solid #e6eef7', marginTop: 6, resize: 'vertical', minHeight: 80 }}
											id="otherChallenge"
											name="otherChallenge"
											value={form.otherChallenge}
											onChange={handleChange}
											rows={4}
											placeholder="Describe your challenge"
											required
										/>
										<div style={{ fontSize: 12, color: '#5A6478', marginTop: 4 }}>
											{form.otherChallenge.trim().split(/\s+/).filter(Boolean).length} / 100 words
										</div>
									</div>
								)}
							</div>
							<button 
								type="submit" 
								className="about-btn" 
								style={{ 
									width: '50%', 
									fontSize: 18, 
									borderRadius: 30, 
									marginTop: 8, 
									marginBottom: 30,
									opacity: loading ? 0.7 : 1,
									cursor: loading ? 'not-allowed' : 'pointer'
								}}
								disabled={loading}
							>
								{loading ? 'Sending...' : 'Schedule Your Free Session'}
							</button>
						</form>
						{submitted && (
							<div style={{ marginTop: 18, padding: '16px', background: '#e6f7f5', border: '2px solid #2EB8A8', borderRadius: 12, color: '#2B4278', textAlign: 'center', fontWeight: 600, fontSize: 16 }}>
								✓ Thank you! Your consultation request has been sent successfully. We'll contact you soon!
							</div>
						)}
						{error && (
							<div style={{ marginTop: 18, padding: '16px', background: '#ffe6e6', border: '2px solid #dc3545', borderRadius: 12, color: '#721c24', textAlign: 'center', fontWeight: 600, fontSize: 16 }}>
								⚠ {error}
							</div>
						)}
					</div>
				</div>
			</>
		);
};

export default Contact;
